import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import { PurgeAllDynamicDataBL } from '../../../../utilities/BL/Cronometer';

/**
 * Modal de Zona Peligrosa y Reinicio Total para la dinámica 'Cronómetro' (Tile ID 50).
 * Protege la integridad de la base de datos solicitando la contraseña maestra oficial
 * ('') para archivar y purgar de forma definitiva los participantes,
 * registros de tiempos de cada fase y métricas de la jornada para el nuevo partido.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Visibilidad del modal.
 * @param {Function} props.onClose - Callback al cerrar.
 * @param {Function} props.onPurgeSuccess - Callback ejecutado tras el reinicio exitoso.
 * @returns {React.ReactElement|null}
 */
export function CronometerDangerModal({
    isOpen,
    onClose,
    onPurgeSuccess
}) {
    const { showMessage } = useFloatMessage();
    const [passwordInput, setPasswordInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPasswordInput('');
            setShowPassword(false);
            setIsSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = passwordInput.trim();
        if (!trimmed) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Ingresa la contraseña maestra para confirmar esta acción crítica.'
            });
            return;
        }

        const validMasterPasswords = ['evitarborrarregistros456852'];
        if (!validMasterPasswords.includes(trimmed)) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Contraseña maestra incorrecta. No tienes autorización para reiniciar la dinámica.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await PurgeAllDynamicDataBL(trimmed, 'Admin');
            if (res && res.success !== false) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡La dinámica de Cronómetro ha sido reiniciada con éxito para el nuevo partido!'
                });
                if (onPurgeSuccess && typeof onPurgeSuccess === 'function') {
                    await onPurgeSuccess();
                }
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res?.message || 'Error al reiniciar la dinámica en el servidor.'
                });
            }
        } catch (error) {
            console.error('Error en reinicio crítico de Cronómetro:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: error.message || 'Error de conexión durante el reinicio total.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="modal show d-block"
            tabIndex="-1"
            style={{
                backgroundColor: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(5px)',
                zIndex: 1080
            }}
        >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden bg-white text-dark">
                    {/* Header */}
                    <div className="modal-header bg-danger text-white border-0 py-3 px-4">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
                            <i className="bi bi-exclamation-triangle-fill fs-4"></i>
                            Zona Peligrosa: Reinicio de Cronómetro
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            aria-label="Cerrar"
                            disabled={isSubmitting}
                        ></button>
                    </div>

                    {/* Body */}
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4 bg-light">
                            <div className="alert alert-danger py-3 px-3 rounded-3 mb-3 border-danger">
                                <h6 className="fw-bold text-danger mb-1 d-flex align-items-center gap-2">
                                    <i className="bi bi-shield-fill-x fs-5"></i>
                                    ¡Acción Crítica e Irreversible!
                                </h6>
                                <p className="small mb-0 text-dark">
                                    Esta operación purgará permanentemente a <strong>todos los participantes registrados</strong>,
                                    los registros de tiempos obtenidos en cada fase y pondrá en cero el embudo de telemetría y los contadores en tiempo real para el nuevo partido.
                                </p>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold text-dark small">
                                    Contraseña Maestra Oficial *
                                </label>
                                <div className="input-group">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className="form-control"
                                        placeholder="Ingresa la contraseña maestra..."
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        required
                                        autoFocus
                                        disabled={isSubmitting}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex="-1"
                                        disabled={isSubmitting}
                                    >
                                        <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                    </button>
                                </div>
                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    Protección administrativa contra borrado accidental de registros de jornada.
                                </small>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer bg-light border-top px-4 py-3 d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold px-4"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-danger fw-bold d-flex align-items-center gap-2 px-4 shadow-sm"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Reiniciando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-trash-fill"></i>
                                        Confirmar y Reiniciar Dinámica
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default CronometerDangerModal;
